import { evaluateExpression } from '@framework/engine/schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '@framework/engine/schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '@framework/engine/schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '@framework/engine/schema/expressions/references/collectExpressionReferences.ts';
import { type ICreateWidgetFormSchemaInput } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { FormBuilderMetaFlags } from '@framework/engine/validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { getWidgetName } from '@framework/engine/validator/form-builder/getWidgetName.ts';
import { isDraftValidation } from '@framework/engine/validator/form-builder/types/FormBuilderTypes.ts';
import { parseAdvancedRichTextToString } from '@framework/engine/validator/utils/parseAdvancedRichTextToString.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { type IProvinceWidget } from './IProvinceWidget.ts';

export const getProvinceFieldName = (code: string) => `${code}@provincia`;
export const getTownFieldName = (code: string) => `${code}@comune`;

export const getProvinceDataFieldName = (code: string) =>
  `${code}@datiProvincia`;
export const getTownDataFieldName = (code: string) => `${code}@datiComune`;

export function getProvinceWidgetFieldNames(widget: IProvinceWidget) {
  const provinceFieldName = getProvinceFieldName(getWidgetName(widget));
  const townFieldName = getTownFieldName(getWidgetName(widget));
  const provinceDataFieldName = getProvinceDataFieldName(getWidgetName(widget));
  const townDataFieldName = getTownDataFieldName(getWidgetName(widget));

  return {
    provinceFieldName,
    townFieldName,
    provinceDataFieldName,
    townDataFieldName,
  };
}

export function createProvinceWidgetSchema(
  input: ICreateWidgetFormSchemaInput<IProvinceWidget>,
) {
  const {
    provinceDataFieldName,
    provinceFieldName,
    townDataFieldName,
    townFieldName,
  } = getProvinceWidgetFieldNames(input.widget);

  const defaultProvinceExpression = input.widget.defaultProvince;
  const defaultProvinceReferences = collectExpressionReferences(
    defaultProvinceExpression,
  );
  const defaultProvincePaths = defaultProvinceReferences.map(r =>
    mapReferenceToPath(r),
  );

  const defaultTownExpression = input.widget.defaultTown;
  const defaultTownReferences = collectExpressionReferences(
    defaultTownExpression,
  );
  const defaultTownPaths = defaultTownReferences.map(r =>
    mapReferenceToPath(r),
  );

  return {
    [provinceFieldName]: yup
      .string()
      .notRequired()
      // Gestiamo il default custom
      .parentWhen(defaultProvincePaths, (values, schema) => {
        if (defaultProvinceExpression == null) return schema;
        const variables = createEvaluationVariables(
          defaultProvinceReferences,
          values,
        );
        const result = evaluateExpression(defaultProvinceExpression, {
          variables,
        });
        if (result != null && typeof result === 'string') {
          return schema.default(result);
        }
        return schema;
      })
      .label(parseAdvancedRichTextToString(input.widget.provinceLabel ?? []))
      // Tecnicamente non supporta proprio il default.
      .formBuilderFlags(FormBuilderMetaFlags.SkipDefaultValue),
    [townFieldName]: yup
      .string()
      .notRequired()
      .label(parseAdvancedRichTextToString(input.widget.townLabel ?? []))
      .when(provinceFieldName, (value, schema, options) => {
        const province: string | null | undefined = value[0];

        // Se non c'è la provincia, rimuovo il campo
        if (!province) return schema.transform(() => null);

        // In bozza non faccio nessuna validazione
        if (isDraftValidation(options.context)) return schema;

        // Se c'è la provincia, rendo il campo obbligatorio
        return schema.required();
      })
      // Gestiamo il default custom
      .parentWhen(defaultTownPaths, (values, schema) => {
        if (defaultTownExpression == null) return schema;
        const variables = createEvaluationVariables(
          defaultTownReferences,
          values,
        );
        const result = evaluateExpression(defaultTownExpression, {
          variables,
        });
        if (result != null && typeof result === 'string') {
          return schema.default(result);
        }
        return schema;
      }),
    [provinceDataFieldName]: yup
      .object({
        nome: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.prov-name', 'Nome provincia')),
        regione: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.prov-region', 'Regione provincia')),
        lat: yup
          .number()
          .notRequired()
          .label(t('province-widget-schema.prov-lat', 'Latitudine provincia')),
        lng: yup
          .number()
          .notRequired()
          .label(t('province-widget-schema.prov-lng', 'Longitudine provincia')),
        codiceBelfiore: yup
          .string()
          .notRequired()
          .label(
            t('province-widget-schema.prov-belfiore-code', 'Codice Belfiore'),
          ),
      })
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipValidationRules,
        FormBuilderMetaFlags.SkipDefaultValue,
      ),
    [townDataFieldName]: yup
      .object({
        nome: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.mun-name', 'Nome comune')),
        provincia: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.mun-province', 'Provincia comune')),
        regione: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.mun-region', 'Regione comune')),
        cap: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.mun-cap', 'Cap comune')),
        codiceIstat: yup
          .string()
          .notRequired()
          .label(t('province-widget-schema.mun-istat', 'Codice Istat comune')),
        lat: yup
          .number()
          .notRequired()
          .label(t('province-widget-schema.mun-lat', 'Latitudine comune')),
        lng: yup
          .number()
          .notRequired()
          .label(t('province-widget-schema.mun-lng', 'Longitudine comune')),
        codiceBelfiore: yup
          .string()
          .notRequired()
          .label(
            t(
              'province-widget-schema.mun-belfiore.code',
              'Codice Belfiore comune',
            ),
          ),
      })
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipValidationRules,
        FormBuilderMetaFlags.SkipDefaultValue,
      ),
  };
}
